/**
 * Wraps a StickerEmblem so tapping it opens a small, kid-friendly card
 * explaining how the sticker is earned — in language Eva (age 6) can read.
 *
 * Only the My Stickers screen (the grid + the mastery carousel) uses this.
 * StickerEmblem itself stays a pure presentational component so it keeps
 * rendering identically everywhere else (celebration, alphabet page,
 * What's New preview) — the tap target, modal state and explanation copy
 * all live here.
 *
 * Locked streak / total stickers render as blank silhouettes that
 * deliberately hide their milestone number (spec §8, "surprise reward").
 * Their card stays vague on purpose — "a secret sticker, keep practising".
 * Earned stickers get the specific story of what Eva did to earn them.
 */
import { useState } from "react";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { StickerEmblem } from "@/components/StickerEmblem";
import type { StickerEmblemProps } from "@/components/StickerEmblem";
import { explainSticker } from "@/lib/stickerExplanations";
import "./ExplainableSticker.css";

export function ExplainableSticker(props: StickerEmblemProps) {
  const [open, setOpen] = useState(false);
  const titleId = `sticker-info-${props.stickerId}`;
  const { title, body } = explainSticker(props);
  const close = () => setOpen(false);

  return (
    <>
      <button
        type="button"
        className="explain-sticker"
        onClick={() => setOpen(true)}
        aria-label={buttonLabel(props)}
      >
        <StickerEmblem {...props} />
      </button>

      <Modal open={open} onClose={close} titleId={titleId}>
        <div className="sticker-info">
          {/* Re-render the emblem larger as the card's hero. We drop the
              "new!" / "× N" chips here — the card is about how it's earned,
              not its current status — but keep silhouette / locked state so
              an unearned sticker still reads as not-yet-earned. */}
          <div className="sticker-info__emblem">
            <StickerEmblem
              category={props.category}
              stickerId={props.stickerId}
              name={props.name}
              {...(props.silhouette !== undefined && { silhouette: props.silhouette })}
              {...(props.locked !== undefined && { locked: props.locked })}
            />
          </div>
          <h2 id={titleId} className="sticker-info__title">
            {title}
          </h2>
          {body && <p className="sticker-info__body">{body}</p>}
          <Button size="large" onClick={close}>
            Got it!
          </Button>
        </div>
      </Modal>
    </>
  );
}

/** Accessible name for the tap target. Locked silhouettes stay vague so a
 *  screen reader doesn't announce a milestone the art is hiding. */
function buttonLabel(props: StickerEmblemProps): string {
  if (props.silhouette) return "Secret sticker. Tap to learn more.";
  return `${props.name} sticker. Tap to learn about it.`;
}
